// Chapter 26 - Antecedent Interventions
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 26.

import type { ChapterModule } from "./types";

const chapter26Module: ChapterModule = {
  meta: {
    id: "26",
    number: 26,
    title: "Antecedent Interventions",
    subtitle: "NCR, high-p sequences, functional communication training",
    part: "Part 9 - Decreasing Behavior with Nonpunishment Procedures",
    estMinutes: 60,
    bacb: ["G-2", "G-4"],
  },
  curated: true,
  overview: [
    "Chapter 26 covers ANTECEDENT INTERVENTIONS - manipulations applied BEFORE the target behavior occurs. The chapter distinguishes FUNCTION-BASED interventions (manipulate at least one component of the maintaining contingency) from DEFAULT interventions (do not depend on knowing the function), and CONTINGENCY-DEPENDENT from CONTINGENCY-INDEPENDENT interventions.",
    "The major function-based tactics covered are NONCONTINGENT REINFORCEMENT (NCR), HIGH-PROBABILITY INSTRUCTIONAL SEQUENCE (behavioral momentum), and FUNCTIONAL COMMUNICATION TRAINING (FCT). Default interventions covered include antecedent exercise, enriched environments, and three restraint variants (personal, protective equipment, self-restraint).",
  ],
  objectives: [
    { id: "G-2", text: "Use motivating operations procedurally to support skill acquisition and reduce challenging behavior." },
    { id: "G-4", text: "Use antecedent interventions including NCR, high-p sequences, and FCT." },
  ],
  coreConcepts: [
    {
      title: "What is an antecedent intervention",
      body: [
        "An antecedent intervention is implemented PRIOR TO and INDEPENDENT OF the target behavior's occurrence. Two broad categories (Smith, 2011): FUNCTION-BASED interventions manipulate at least one component of the maintaining contingency; DEFAULT interventions do not depend on identifying the variables that evoke and maintain the problem behavior.",
      ],
    },
    {
      title: "Contingency-dependent vs contingency-independent",
      body: [
        "Antecedent interventions can also be classified by whether they require differential consequences. CONTINGENCY-INDEPENDENT interventions (NCR, enriched environment) do not depend on differential consequences. CONTINGENCY-DEPENDENT interventions depend on differential consequences for the target behavior or an alternative in the presence/absence of the antecedent.",
      ],
    },
    {
      title: "Noncontingent reinforcement (NCR)",
      body: [
        "NCR delivers stimuli with known reinforcing properties on a FIXED-TIME (FT) or VARIABLE-TIME (VT) schedule independent of the learner's behavior. An NCR-enriched environment functions as an ABOLISHING OPERATION (AO) that reduces motivation to engage in the problem behavior. Functional analysis determines whether NCR uses positive, negative, or automatic reinforcement variants.",
      ],
    },
    {
      title: "High-probability instructional sequence (behavioral momentum)",
      body: [
        "First, present 2-5 high-probability (high-p) instructions with a history of compliance and reinforce each compliant response. Second, immediately after the participant has complied with several high-p instructions, present the LOW-P (target) instruction. Third, gradually fade the ratio of high-p to low-p instructions to 1-to-1. Reductive effects on noncompliance resemble the abative effects of an AO that reduces the value of escape from requests; sometimes called BEHAVIORAL MOMENTUM.",
      ],
    },
    {
      title: "Functional communication training (FCT)",
      body: [
        "FCT establishes an appropriate communicative behavior to compete with problem behaviors evoked by an MO. The practitioner uses DRA to teach an adaptive response that produces the SAME reinforcer that has been maintaining the problem behavior, making the alternative response FUNCTIONALLY EQUIVALENT.",
      ],
    },
    {
      title: "Default antecedent interventions",
      body: [
        "Default interventions do not depend on identifying maintaining variables. (a) ANTECEDENT EXERCISE: aerobic activity prior to an instruction or environment where problem behavior frequently occurs. (b) ENRICHED ENVIRONMENT: noncontingent access to preferred reinforcers arranges competition between the intervention and the stimulation from the problem behavior. (c) RESTRAINT (PERSONAL, PROTECTIVE EQUIPMENT, SELF-RESTRAINT): physically limiting, prohibiting, or securing the individual so the problem behavior cannot occur.",
      ],
    },
  ],
  glossary: [
    { term: "Antecedent intervention", definition: "Intervention implemented PRIOR to and INDEPENDENT of the target behavior's occurrence.", sourceHint: "An antecedent intervention is implemented prior to and independent of the target behavior's occurrence." },
    { term: "Function-based antecedent intervention", definition: "Intervention that manipulates at least one component of the maintaining contingency.", sourceHint: "Function-based antecedent interventions manipulate at least one component of the contingency identified as maintaining the problem behavior." },
    { term: "Default antecedent intervention", definition: "Intervention that does not depend on identifying the variables that evoke and maintain the problem behavior.", sourceHint: "default interventions do not depend on identifying the variables that evoke and maintain the problem behavior." },
    { term: "Contingency-independent intervention", definition: "Intervention that does not require differential consequences (e.g., NCR, enriched environment).", sourceHint: "Contingency-independent antecedent interventions do not depend on differential consequences for the target behavior." },
    { term: "Contingency-dependent intervention", definition: "Intervention requiring differential consequences for the target behavior or an alternative.", sourceHint: "contingency-dependent antecedent interventions depend on differential consequences for the target behavior (or alternative behavior) in the presence or absence of the antecedent." },
    { term: "Noncontingent reinforcement (NCR)", definition: "Delivery of reinforcers on a fixed- or variable-time schedule independent of behavior; functions as an AO.", sourceHint: "NCR is an antecedent intervention in which stimuli with known reinforcing properties are delivered on a fixed-time (FT) or variable-time (VT) schedule independent of the learner's behavior." },
    { term: "High-probability instructional sequence (high-p)", definition: "Sequence of high-probability requests followed by a low-probability request to increase compliance; behavioral momentum.", sourceHint: "the high-probability instructional sequence (high-p)." },
    { term: "Behavioral momentum", definition: "Phenomenon used to describe the effects of high-p instructional sequences.", sourceHint: "Behavioral momentum is sometimes used to describe the effects produced by the high-p instructional sequence." },
    { term: "Functional communication training (FCT)", definition: "Establishes an appropriate communicative behavior that produces the same reinforcer as the problem behavior, via DRA.", sourceHint: "FCT establishes an appropriate communicative behavior to compete with problem behaviors evoked by a motivating operation (MO)." },
    { term: "Antecedent exercise", definition: "Aerobic activity prior to an instruction or environment where problem behavior frequently occurs.", sourceHint: "Antecedent exercise requires the participant to engage in some form of aerobic activity prior to receiving an instruction." },
    { term: "Enriched environment", definition: "Noncontingent access to preferred reinforcers that competes with the stimulation from problem behavior.", sourceHint: "noncontingent access to preferred sources of reinforcement provided by an enriched environment intervention arranges a competition between the intervention and the stimulation provided by the problem behavior." },
    { term: "Personal restraint", definition: "Physical limiting by another person so the problem behavior cannot occur.", sourceHint: "personal restraint, protective equipment restraint, and self-restraint." },
    { term: "Protective equipment restraint", definition: "Use of physical equipment (helmets, gloves) to prevent the problem behavior.", sourceHint: "protective equipment restraint." },
    { term: "Self-restraint", definition: "The individual's own behavior limits their problem behavior (e.g., sitting on hands).", sourceHint: "self-restraint." },
  ],
  procedures: [
    {
      title: "Implement NCR",
      intro: "Decide schedule and reinforcer; deliver independent of behavior.",
      steps: [
        { title: "FBA confirms function", detail: "Identify the maintaining reinforcer." },
        { title: "Pick schedule", detail: "FT or VT; start with a dense schedule then thin." },
        { title: "Deliver independent of behavior", detail: "Reinforce on the schedule regardless of what the learner is doing." },
        { title: "Pair with extinction where feasible", detail: "Block the problem behavior from producing the reinforcer." },
        { title: "Thin schedule", detail: "Gradually lengthen interval as the problem behavior decreases." },
      ],
    },
    {
      title: "Run a high-p instructional sequence",
      intro: "Build behavioral momentum before a low-p request.",
      steps: [
        { title: "Identify 2-5 high-p instructions", detail: "Instructions the learner has a history of complying with." },
        { title: "Deliver and reinforce", detail: "Present each high-p; reinforce compliance immediately." },
        { title: "Present low-p target", detail: "After the high-p sequence, deliver the low-p instruction; reinforce compliance." },
        { title: "Fade ratio", detail: "Gradually shift the ratio of high-p to low-p toward 1-to-1." },
      ],
    },
    {
      title: "Implement FCT",
      intro: "Teach an alternative communicative response that produces the same reinforcer.",
      steps: [
        { title: "FBA confirms function", detail: "Identify the reinforcer maintaining the problem behavior." },
        { title: "Select communicative response", detail: "Pick a topography that is appropriate, easy, and reliably recognized." },
        { title: "Teach with DRA", detail: "Reinforce the new response with the maintaining reinforcer; withhold for the problem behavior." },
        { title: "Generalize and thin", detail: "Extend across people and settings; thin reinforcement gradually." },
      ],
    },
  ],
  examples: [
    { scenario: "A child whose tantrums are attention-maintained receives 30 seconds of teacher attention every 2 minutes regardless of behavior.",
      application: "Noncontingent reinforcement (FT schedule); abolishing operation reduces motivation for tantrums.",
      sourceHint: "An NCR-enriched environment may function as an abolishing operation (AO) that reduces motivation to engage in the problem behavior." },
    { scenario: "Therapist asks 'Touch nose', 'Touch head', 'Touch chest', then 'Put on shoes.' Compliance with shoes increases.",
      application: "High-p instructional sequence; behavioral momentum.",
      sourceHint: "Behavioral momentum is sometimes used to describe the effects produced by the high-p instructional sequence." },
    { scenario: "Child who tantrums for attention is taught to tap an adult and say 'Talk please'; tantrums decrease as the tap-and-request increases.",
      application: "FCT - teaches a functionally equivalent communication response.",
      sourceHint: "FCT establishes an appropriate communicative behavior to compete with problem behaviors evoked by a motivating operation (MO)." },
    { scenario: "Before a difficult academic task, the learner runs three laps; problem behavior during the task decreases.",
      application: "Antecedent exercise; a default antecedent intervention.",
      sourceHint: "Antecedent exercise requires the participant to engage in some form of aerobic activity prior to receiving an instruction or entering an environment in which the problem behavior has frequently occurred." },
  ],
  comparisons: [
    {
      title: "Function-based vs Default antecedent interventions",
      left:  { label: "Function-based (NCR, high-p, FCT)", points: [
        "Manipulate at least one component of the maintaining contingency.",
        "Require FBA.",
      ]},
      right: { label: "Default (exercise, enrichment, restraint)", points: [
        "Do not depend on knowing the maintaining contingency.",
        "Used when function is unknown or unconfirmed.",
      ]},
    },
    {
      title: "Contingency-independent vs Contingency-dependent",
      left:  { label: "Contingency-independent", points: [
        "Effect does not depend on differential consequences.",
        "NCR, enriched environment.",
      ]},
      right: { label: "Contingency-dependent", points: [
        "Effect depends on differential consequences.",
        "FCT (via DRA), high-p (reinforced compliance).",
      ]},
    },
    {
      title: "NCR vs Extinction",
      left:  { label: "NCR", points: [
        "Reinforcer is delivered REGARDLESS of behavior.",
        "Functions as an AO.",
      ]},
      right: { label: "Extinction", points: [
        "Reinforcer is withheld when the behavior occurs.",
        "Produces direct decline in responding.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "NCR teaches the learner the problem behavior is okay.",
      reality: "NCR delivers the maintaining reinforcer noncontingently, reducing the MO for the problem behavior. Pair with extinction where feasible for stronger effect.",
      sourceHint: "An NCR-enriched environment may function as an abolishing operation (AO) that reduces motivation to engage in the problem behavior.",
    },
    {
      myth: "High-p sequences only work with simple compliance.",
      reality: "Behavioral momentum can be applied across a wide range of low-p targets; the key is genuine high-p history and immediate reinforcement.",
      sourceHint: "Behavioral momentum is sometimes used to describe the effects produced by the high-p instructional sequence.",
    },
    {
      myth: "FCT replaces extinction.",
      reality: "FCT IS a DRA-based procedure that teaches a functionally equivalent response while extinguishing the problem behavior whenever possible.",
      sourceHint: "The practitioner uses DRA to teach an adaptive response that produces the same reinforcer that has maintained the problem behavior, thereby making the alternative communicative response functionally equivalent to the problem behavior.",
    },
    {
      myth: "Restraint is a behavior-change intervention.",
      reality: "Restraint is an antecedent default intervention that PREVENTS the behavior; it does not change the underlying contingency. Use only with explicit ethical safeguards.",
      sourceHint: "Three methods of restraint - physically limiting, prohibiting, or securing an individual in such a manner that the problem behavior cannot occur - have been evaluated in the applied behavior analysis literature.",
    },
  ],
  examChecklist: [
    "Define antecedent intervention and the function-based vs default distinction.",
    "Distinguish contingency-independent vs contingency-dependent interventions.",
    "Describe NCR and identify the schedule types (FT, VT).",
    "Describe the high-p instructional sequence and behavioral momentum.",
    "Describe FCT and explain its DRA basis.",
    "List three default antecedent interventions with one example each.",
    "Describe the three restraint variants.",
    "Explain why NCR can function as an AO.",
  ],
  flashcardTerms: [
    "Antecedent intervention",
    "Function-based antecedent intervention", "Default antecedent intervention",
    "Contingency-independent intervention", "Contingency-dependent intervention",
    "Noncontingent reinforcement (NCR)",
    "High-probability instructional sequence (high-p)", "Behavioral momentum",
    "Functional communication training (FCT)",
    "Antecedent exercise", "Enriched environment",
    "Personal restraint", "Protective equipment restraint", "Self-restraint",
  ],
  quiz: [
    {
      id: "Q1",
      question: "NCR primarily functions to reduce problem behavior by:",
      options: [
        { label: "A", text: "Punishing the problem behavior" },
        { label: "B", text: "Acting as an abolishing operation that reduces motivation for the problem behavior" },
        { label: "C", text: "Reinforcing the problem behavior" },
        { label: "D", text: "Teaching a new response" },
      ],
      answer: "B",
      explanation: "NCR provides the maintaining reinforcer noncontingently, abolishing the value of the reinforcer for the moment.",
      topic: "NCR",
      sourceHint: "An NCR-enriched environment may function as an abolishing operation (AO) that reduces motivation to engage in the problem behavior.",
    },
    {
      id: "Q2",
      question: "The high-probability instructional sequence works by:",
      options: [
        { label: "A", text: "Using punishment for noncompliance" },
        { label: "B", text: "Building behavioral momentum with reinforced high-p instructions before a low-p target" },
        { label: "C", text: "Extinguishing the low-p target" },
        { label: "D", text: "Restraining the learner" },
      ],
      answer: "B",
      explanation: "Several reinforced high-p compliances build momentum that carries into the next, low-p request.",
      topic: "High-p sequence",
      sourceHint: "the high-probability instructional sequence (high-p) is as follows: First, present two to five instructions for which the participant has a history of compliance and reinforce each compliant response. Second, immediately after the participant has complied with several high-p instructions in sequence, present a target (i.e., low-p) instruction.",
    },
    {
      id: "Q3",
      question: "FCT is best described as:",
      options: [
        { label: "A", text: "A reinforcement schedule" },
        { label: "B", text: "A DRA procedure that teaches a communicative response producing the same reinforcer as the problem behavior" },
        { label: "C", text: "A type of restraint" },
        { label: "D", text: "An NCR variant" },
      ],
      answer: "B",
      explanation: "FCT uses DRA to teach a functionally equivalent communication response.",
      topic: "FCT",
      sourceHint: "The practitioner uses DRA to teach an adaptive response that produces the same reinforcer that has maintained the problem behavior, thereby making the alternative communicative response functionally equivalent to the problem behavior.",
    },
    {
      id: "Q4",
      question: "Which is NOT a default antecedent intervention?",
      options: [
        { label: "A", text: "Antecedent exercise" },
        { label: "B", text: "Enriched environment" },
        { label: "C", text: "Personal restraint" },
        { label: "D", text: "Functional communication training" },
      ],
      answer: "D",
      explanation: "FCT is a FUNCTION-BASED intervention. Default interventions do not depend on identifying the maintaining variables.",
      topic: "Default vs function-based",
      sourceHint: "Default antecedent interventions do not depend on identifying the variables that evoke and maintain the problem behavior.",
    },
    {
      id: "Q5",
      question: "Which type of intervention does NOT require differential consequences?",
      options: [
        { label: "A", text: "Contingency-dependent" },
        { label: "B", text: "Contingency-independent" },
        { label: "C", text: "Function-based" },
        { label: "D", text: "Behavioral momentum" },
      ],
      answer: "B",
      explanation: "Contingency-independent interventions like NCR or enriched environment do not require differential consequences for the target behavior.",
      topic: "Contingency classification",
      sourceHint: "Contingency-independent antecedent interventions do not depend on differential consequences for the target behavior.",
    },
  ],
  reviewPrompts: [
    "Plan an NCR intervention for an attention-maintained problem behavior.",
    "Sketch a high-p instructional sequence for a difficult academic task.",
    "Design an FCT program for an escape-maintained behavior.",
    "Distinguish function-based from default antecedent interventions in two real examples.",
    "List four ethical safeguards required for any restraint procedure.",
  ],
  recap: [
    "Antecedent interventions are applied BEFORE the problem behavior occurs. Function-based interventions manipulate the maintaining contingency; default interventions do not require knowing the function. Contingency-independent vs contingency-dependent classifies whether differential consequences are required.",
    "Function-based tactics: NCR (delivers reinforcer on FT/VT schedule, AO effect), high-p instructional sequence (behavioral momentum), and FCT (DRA-based teaching of a functionally equivalent communicative response).",
    "Default tactics: antecedent exercise, enriched environment, and restraint variants. Restraint is preventive, not behavior-changing - ethical safeguards required. Combine antecedent interventions with reductive consequence-based procedures and skill teaching for durable change.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition and classification",
      text: "An antecedent intervention is implemented prior to and independent of the target behavior's occurrence. Smith (2011) identified two broad categories of antecedent interventions: Function-based antecedent interventions manipulate at least one component of the contingency identified as maintaining the problem behavior; default interventions do not depend on identifying the variables that evoke and maintain the problem behavior.",
    },
    {
      heading: "NCR",
      text: "NCR is an antecedent intervention in which stimuli with known reinforcing properties are delivered on a fixed-time (FT) or variable-time (VT) schedule independent of the learner's behavior. An NCR-enriched environment may function as an abolishing operation (AO) that reduces motivation to engage in the problem behavior.",
    },
    {
      heading: "High-p sequence",
      text: "The basic procedure for implementing the high-probability instructional sequence (high-p) is as follows: First, present two to five instructions for which the participant has a history of compliance and reinforce each compliant response. Second, immediately after the participant has complied with several high-p instructions in sequence, present a target (i.e., low-p) instruction. Third, gradually fade the ratio of high-p to low-p instructions to 1-to-1.",
    },
    {
      heading: "FCT",
      text: "FCT establishes an appropriate communicative behavior to compete with problem behaviors evoked by a motivating operation (MO). The practitioner uses DRA to teach an adaptive response that produces the same reinforcer that has maintained the problem behavior, thereby making the alternative communicative response functionally equivalent to the problem behavior.",
    },
  ],
};

export default chapter26Module;
